import { Color } from '@draughts/shared';
import type { VariantRulesSpec } from '@draughts/shared';

export interface Direction {
  row: number;
  col: number;
}

export const DIAGONAL_DIRS: readonly Direction[] = [
  { row: -1, col: -1 },
  { row: -1, col: +1 },
  { row: +1, col: -1 },
  { row: +1, col: +1 },
];

export const ORTHOGONAL_DIRS: readonly Direction[] = [
  { row: -1, col: 0 },
  { row: +1, col: 0 },
  { row: 0, col: -1 },
  { row: 0, col: +1 },
];

// Dark-squares-only boards (e.g. Frisian/Frysk 10x10): orthogonally-adjacent
// PLAYING squares are 2 grid-steps apart (the unit step lands on a light,
// non-playable square). Captures therefore hop with a step-2 orthogonal vector.
export const ORTHOGONAL_DIRS_DARK: readonly Direction[] = [
  { row: -2, col: 0 },
  { row: +2, col: 0 },
  { row: 0, col: -2 },
  { row: 0, col: +2 },
];

const DIAGONAL_FORWARD: Record<Color, readonly Direction[]> = {
  [Color.WHITE]: [
    { row: -1, col: -1 },
    { row: -1, col: +1 },
  ],
  [Color.BLACK]: [
    { row: +1, col: -1 },
    { row: +1, col: +1 },
  ],
};

const ORTHOGONAL_FORWARD: Record<Color, readonly Direction[]> = {
  [Color.WHITE]: [
    { row: -1, col: 0 },
    { row: 0, col: -1 },
    { row: 0, col: +1 },
  ],
  [Color.BLACK]: [
    { row: +1, col: 0 },
    { row: 0, col: -1 },
    { row: 0, col: +1 },
  ],
};

// Step-2 orthogonal-forward vectors for dark-squares-only boards (see ORTHOGONAL_DIRS_DARK).
const ORTHOGONAL_FORWARD_DARK: Record<Color, readonly Direction[]> = {
  [Color.WHITE]: [
    { row: -2, col: 0 },
    { row: 0, col: -2 },
    { row: 0, col: +2 },
  ],
  [Color.BLACK]: [
    { row: +2, col: 0 },
    { row: 0, col: -2 },
    { row: 0, col: +2 },
  ],
};

const DIAGONAL_FORWARD_ONLY: Record<Color, readonly Direction[]> = DIAGONAL_FORWARD;

export function getManMoveDirections(color: Color, rules: VariantRulesSpec): readonly Direction[] {
  if (rules.movement === 'orthogonal') {
    // Pedine ortogonali (Turkish/Armenian/Dameo): avanti + lati, mai indietro.
    // Allineato alle direzioni di cattura (getManCaptureDirections).
    return ORTHOGONAL_FORWARD[color];
  }
  return DIAGONAL_FORWARD_ONLY[color];
}

export function getManCaptureDirections(
  color: Color,
  rules: VariantRulesSpec,
  allSquares: boolean,
): readonly Direction[] {
  const includeBackward = rules.menCaptureBackward;
  // On dark-squares-only boards the orthogonal hop steps by 2 (stay on playing squares).
  const ortho = allSquares ? ORTHOGONAL_DIRS : ORTHOGONAL_DIRS_DARK;
  const orthoForward = allSquares ? ORTHOGONAL_FORWARD[color] : ORTHOGONAL_FORWARD_DARK[color];

  if (rules.captureType === 'orthogonal') {
    return includeBackward ? ortho : orthoForward;
  }

  if (rules.captureType === 'mixed') {
    if (includeBackward) {
      return [...DIAGONAL_DIRS, ...ortho];
    }
    return [...DIAGONAL_FORWARD[color], ...orthoForward];
  }

  return includeBackward ? DIAGONAL_DIRS : DIAGONAL_FORWARD[color];
}

export function getKingMoveDirections(rules: VariantRulesSpec): readonly Direction[] {
  if (rules.movement === 'orthogonal') {
    return ORTHOGONAL_DIRS;
  }
  return DIAGONAL_DIRS;
}

export function getKingCaptureDirections(
  rules: VariantRulesSpec,
  allSquares: boolean,
): readonly Direction[] {
  const ortho = allSquares ? ORTHOGONAL_DIRS : ORTHOGONAL_DIRS_DARK;
  if (rules.captureType === 'orthogonal') {
    return ortho;
  }
  if (rules.captureType === 'mixed') {
    return [...DIAGONAL_DIRS, ...ortho];
  }
  return DIAGONAL_DIRS;
}
